import React from 'react';
import PropTypes from 'prop-types';
import {filter, get, map, orderBy, startCase} from 'lodash';

// Components
import FormButtons from '../core/form-buttons';
import Input from '../core/input';
import LockedIndicator from '../app/locked-indicator';
import Permissions from '../core/permissions';
import SearchPagination from '../app/search-pagination';
import SearchTableWithSort from '../app/search-tablewithsort';
import Select from '../core/select';
import ServicesExportPanelAction from '../app/services-export-panel-action';
import ServicesSearchForm from '../app/services-search-form';
import ServicesSearchPanelAction from '../app/services-search-panel-action';
import Thumbnail from '../core/thumbnail';

// Containers
import withSearch from '../../containers/with-search';

// Styles
import './display-paths.scss';

const servicesDataKey = 'displayPaths';

function DisplayPaths(props) {
    const {query, searchData, totalPages} = props;

    const getColumnConfig = () => [
        {
            dataKey: '',
            type: 'component',
            component: LockedIndicator,
            componentProps: getLockedIndicatorProps,
            width: '3%',
        },
        {
            dataKey: 'displayPath',
            link: '/display-paths',
            linkKey: 'networkDisplayPathId',
            title: 'Display Path',
            type: 'link',
            width: '40%',
        },
        {
            dataKey: 'status',
            title: 'Status',
            width: '15%',
        },
        {
            dataKey: 'adult',
            title: 'Adult Content',
            width: '15%',
        },
        {
            dataKey: 'adultLevel',
            title: 'Adult Level',
            width: '12%',
        },
        {
            dataKey: 'network.name',
            title: 'Network',
            width: '15%',
        },
    ];

    const getPaginationProps = () => ({
        maxPages: 9,
        query,
        totalPages,
    });

    const getTablewithsortProps = () => ({
        ...props,
        activeRowKeys: ['networkDisplayPathId'],
        columnConfig: getColumnConfig(),
        rowDetails: {
            whitelistKeys: [
                'dateCreated',
                'createdBy',
                'lastUpdated',
                'lastModifiedBy',
                'version',
                'networkDisplayPathId',
                'network',
                'displayPath',
                'status',
                'adult',
                'adultLevel',
                'amsProgram.echostarId',
                'amsProgram.title',
                'amsProgram.description',
                'amsProgram.seriesId',
                'amsProgram.seriesTitle',
                'amsProgram.images',
            ],
            footerRenderer: renderImages,
        },
        hideOnError: true,
        servicesDataKey,
        formName: servicesDataKey,
    });

    const renderImages = (searchData) => {
        const amsImages = orderBy(filter(get(searchData, '0.amsProgram.images', []), 'url'), 'category');
        const networkDisplayPathImages = orderBy(
            filter(get(searchData, '0.images', []), 'finalResourceName'),
            'category'
        );

        return (
            <>
                {amsImages.length ? (
                    <>
                        <label className="display-paths--title">Ams Images: </label>
                        <div className="display-paths--thumbnail-group">
                            {map(amsImages, ({amsGalleryId, category, height, url, width}) => (
                                <Thumbnail
                                    className="display-paths--thumbnail"
                                    height={height}
                                    key={amsGalleryId}
                                    label={startCase(category)}
                                    src={url}
                                    width={width}
                                />
                            ))}
                        </div>
                    </>
                ) : null}

                {networkDisplayPathImages.length ? (
                    <>
                        <label className="display-paths--title">Display Path Images: </label>
                        <div className="display-paths--thumbnail-group">
                            {map(networkDisplayPathImages, ({category, finalResourceName, imageId}) => (
                                <Thumbnail
                                    className="display-paths--thumbnail"
                                    key={imageId}
                                    label={
                                        category === 'Franchise'
                                            ? 'Franchise 4:3'
                                            : category === 'SeriesBackground16x9'
                                              ? 'Background (Hero) 16:9'
                                              : category === 'Series16x9'
                                                ? 'Widescreen 16:9'
                                                : startCase(category)
                                    }
                                    src={finalResourceName}
                                />
                            ))}
                        </div>
                    </>
                ) : null}
            </>
        );
    };

    const renderForm = () => (
        <ServicesSearchForm formName={servicesDataKey} servicesDataKey={servicesDataKey}>
            <Select label="Status" name="status">
                <option value="">All</option>
                <option value="ACCEPTED">Accepted</option>
                <option value="REJECTED">Rejected</option>
                <option value="UNKNOWN">Unknown</option>
            </Select>
            <Input label="Display Path" name="displayPath" title="Use wildcard (*) for partial display path match" />
            <Select label="Adult" name="adult">
                <option value="">All</option>
                <option value="true">Adult</option>
                <option value="false">Non-Adult</option>
            </Select>
            <Input label="Adult Level" name="adultLevel" validate={['number']} />
            <FormButtons />
        </ServicesSearchForm>
    );

    const getLockedIndicatorProps = ({networkDisplayPathId}) => ({
        className: 'display-paths--lock',
        lockId: networkDisplayPathId,
        serviceKey: servicesDataKey,
    });

    return (
        <Permissions
            accessRequirement="read"
            elementService="networkDisplayPath"
            notAuthorizedMessage={true}
            requiresServiceAccess={true}
        >
            <div className="display-paths">
                <ServicesExportPanelAction searchData={searchData} />
                <ServicesSearchPanelAction contentRenderer={renderForm} showPanel={true} />
                <SearchPagination {...getPaginationProps()} />
                <SearchTableWithSort {...getTablewithsortProps()} />
            </div>
        </Permissions>
    );
}

DisplayPaths.propTypes = {
    query: PropTypes.object,
    searchData: PropTypes.array,
    totalPages: PropTypes.number,
};

export default withSearch(DisplayPaths, servicesDataKey);
